import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Shield, Users, Building2, Calendar, FileText, 
  Clock, Plus, ChevronRight, Loader2, Lock, Video,
  MapPin, Edit2, Trash2, Check, X, MoreVertical,
  CalendarDays, ListChecks, FolderOpen, ChevronDown,
  UserPlus, Mail, Phone, Linkedin, Timer, AlertCircle, ExternalLink,
  Bell, CheckSquare, MessageSquare, Star, Upload,
  Download, Eye, Gavel, ClipboardList, PlayCircle, Paperclip,
  Link2, Settings, RefreshCw
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import DocumentsTab, { BoardSettingsDialog, UploadDocumentDialog } from './BoardDocuments';
import MinutesPanel from './meeting/MinutesPanel';
import { getCommitteeColor } from '../utils/committeeColors';

const formatIcsDate = (date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

const escapeIcsText = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
};

const buildMeetingIcs = (meeting) => {
  const start = new Date(meeting.scheduled_start);
  const end = meeting.scheduled_end ? new Date(meeting.scheduled_end) : new Date(start.getTime() + 60 * 60 * 1000);
  const locationParts = [];
  if (meeting.location) locationParts.push(meeting.location);
  if (meeting.video_link) locationParts.push(`Video: ${meeting.video_link}`);
  const location = locationParts.join(' | ');
  const description = meeting.description || '';
  const uid = `meeting-${meeting.id}@athena`;
  const dtstamp = formatIcsDate(new Date());

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Athena//Board Meetings//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${formatIcsDate(start)}`,
    `DTEND:${formatIcsDate(end)}`,
    `SUMMARY:${escapeIcsText(meeting.title)}`,
    `DESCRIPTION:${escapeIcsText(description)}`,
    `LOCATION:${escapeIcsText(location)}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
};

const downloadMeetingIcs = (meeting) => {
  const ics = buildMeetingIcs(meeting);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${meeting.title || 'meeting'}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const openCalendarSubscription = async () => {
  try {
    const res = await api.get('/board/calendar/subscription-url');
    const url = res?.data?.subscription_url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    toast.error('Failed to load calendar subscription link');
  } catch (err) {
    console.error('Failed to open calendar subscription:', err);
    toast.error('Failed to load calendar subscription link');
  }
};

const Governance = ({ user, currentOrganization }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const organizationId = currentOrganization?.id || null;
  const queryTab = new URLSearchParams(location.search).get('tab');
  const pathTab = location.pathname.startsWith('/governance/documents') ? 'documents' : null;
  const requestedTab = queryTab || pathTab;
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('board');
  const [access, setAccess] = useState({
    board_enabled: false,
    committees_enabled: false,
    has_board_access: false,
    committees: [],
    show_governance_nav: false,
    user_role: 'viewer'
  });
  const [boardMembers, setBoardMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [messageLoading, setMessageLoading] = useState({});
  const hasAutoRedirected = useRef(false);

  const fetchAccess = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/governance/access');
      setAccess(response.data);
      
      const allowedTabs = new Set();
      if (response.data.board_enabled && response.data.has_board_access) {
        ['board', 'documents', 'calendar', 'members'].forEach(t => allowedTabs.add(t));
      }
      if (response.data.committees_enabled) {
        allowedTabs.add('committees');
      }

      if (requestedTab && allowedTabs.has(requestedTab)) {
        setActiveTab(requestedTab);
      } else if (response.data.has_board_access && response.data.board_enabled) {
        setActiveTab('board');
      } else if (response.data.committees?.length > 0 && response.data.committees_enabled) {
        if (response.data.committees.length === 1 && !hasAutoRedirected.current) {
          hasAutoRedirected.current = true;
          navigate(`/governance/committees/${response.data.committees[0].id}`, { replace: true });
          return;
        }
        setActiveTab('committees');
      }
    } catch (err) {
      console.error('Failed to fetch governance access:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId, requestedTab, navigate]);

  useEffect(() => {
    fetchAccess();
    // Ensure page starts at the top when navigating to Governance
    window.scrollTo(0, 0);
  }, [fetchAccess]);

  useEffect(() => {
    if (!access.has_board_access || !access.board_enabled) return;
    const fetchBoardMembers = async () => {
      setMembersLoading(true);
      try {
        const response = await api.get('/board/documents/members');
        setBoardMembers(response.data?.members || []);
      } catch (err) {
        console.error('Failed to load board members:', err);
        setBoardMembers([]);
      } finally {
        setMembersLoading(false);
      }
    };
    fetchBoardMembers();
  }, [access.has_board_access, access.board_enabled]);

  useEffect(() => {
    if (!organizationId) return;
    setAccess({
      board_enabled: false,
      committees_enabled: false,
      has_board_access: false,
      committees: [],
      show_governance_nav: false,
      user_role: 'viewer'
    });
    setActiveTab(requestedTab || 'board');
  }, [organizationId, requestedTab]);

  // Dialog states - must be before any conditional returns
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const [showUploadDocument, setShowUploadDocument] = useState(false);
  const [showCreateAgenda, setShowCreateAgenda] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [boardRefreshKey, setBoardRefreshKey] = useState(0);

  const tabs = [
    { key: 'board', label: 'Board', icon: Users, show: access.board_enabled && access.has_board_access },
    { key: 'documents', label: 'Documents', icon: FolderOpen, show: access.board_enabled && access.has_board_access },
    { key: 'calendar', label: 'Calendar', icon: Calendar, show: access.board_enabled && access.has_board_access },
    { key: 'committees', label: 'Committees', icon: Building2, show: access.committees_enabled },
    { key: 'members', label: 'Members', icon: Users, show: access.board_enabled && access.has_board_access },
  ].filter(t => t.show);

  const isAdmin = ['owner', 'admin'].includes(access.user_role);
  const canUploadBoardDocuments = isAdmin || access.user_role === 'board_member' || access.has_board_access;

  const openDirectMessage = async (member) => {
    if (!member?.id) return;
    setMessageLoading(prev => ({ ...prev, [member.id]: true }));
    try {
      const response = await api.post('/conversations', {
        type: 'direct',
        participant_ids: [member.id]
      });
      const conversationId = response.data?.id;
      if (conversationId) {
        sessionStorage.setItem('pendingConversation', conversationId);
        window.dispatchEvent(new CustomEvent('openConversation', { detail: conversationId }));
      }
      navigate('/organization/messaging');
    } catch (err) {
      console.error('Failed to open direct message:', err);
      toast.error(err.response?.data?.detail || 'Failed to open message');
    } finally {
      setMessageLoading(prev => ({ ...prev, [member.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="governance-loading">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        <span className="ml-3 text-gray-600">Loading governance...</span>
      </div>
    );
  }

  if (!access.show_governance_nav) {
    return (
      <div className="flex flex-col items-center justify-center py-20" data-testid="governance-no-access">
        <div className="p-4 bg-gray-100 rounded-full mb-4">
          <Lock className="h-12 w-12 text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Access</h2>
        <p className="text-gray-500 text-center max-w-md">
          You don't have access to governance features. Contact your administrator 
          if you believe you should have access to the Board or Committees.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex-1 w-full" data-testid="governance-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className={`mx-auto px-4 sm:px-6 lg:px-8 ${activeTab === 'documents' ? '' : 'max-w-7xl'}`}>
          <div className="py-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Governance</h1>
                  <p className="text-sm text-gray-500">
                    Board of Directors & Committee Management
                  </p>
                </div>
              </div>

              {/* New Button */}
              {(isAdmin || canUploadBoardDocuments) && (
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate('/governance/settings')}
                      data-testid="governance-settings-btn"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Settings
                    </Button>
                  )}
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0" data-testid="new-action-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        New
                        <ChevronDown className="h-4 w-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => setShowCreateMeeting(true)}>
                        <CalendarDays className="h-4 w-4 mr-2" />
                        Schedule Meeting
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => setShowUploadDocument(true)}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Document
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => setShowCreateAgenda(true)}>
                        <ListChecks className="h-4 w-4 mr-2" />
                        Create Agenda
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-gray-200 -mb-px">
              {tabs.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  data-testid={`${key}-tab`}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === key
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`mx-auto px-4 sm:px-6 lg:px-8 py-6 ${activeTab === 'documents' ? 'max-w-full' : 'max-w-7xl'}`}>
        {activeTab === 'board' && access.has_board_access && (
          <BoardDashboard user={user} userRole={access.user_role} refreshKey={boardRefreshKey} />
        )}
        {activeTab === 'documents' && (
          <DocumentsTab userRole={access.user_role} currentOrganizationId={organizationId} />
        )}
        {activeTab === 'calendar' && (
          <CalendarTab userRole={access.user_role} />
        )}
        {activeTab === 'committees' && (
          <CommitteesDashboard 
            user={user} 
            committees={access.committees} 
            userRole={access.user_role}
          />
        )}
        {activeTab === 'members' && access.has_board_access && (
          <BoardMemberProfiles
            members={boardMembers}
            loading={membersLoading}
            onMessage={openDirectMessage}
            messageLoading={messageLoading}
          />
        )}
      </div>

      {/* Global Dialogs */}
      <CreateMeetingDialog 
        open={showCreateMeeting}
        onOpenChange={setShowCreateMeeting}
        onSuccess={() => {
          setShowCreateMeeting(false);
          toast.success('Meeting scheduled successfully');
        }}
      />

      <UploadDocumentDialog
        open={showUploadDocument}
        onOpenChange={setShowUploadDocument}
        currentFolderId={null}
        onSuccess={() => {
          setShowUploadDocument(false);
          setBoardRefreshKey(key => key + 1);
        }}
      />

      <CreateAgendaDialog
        open={showCreateAgenda}
        onOpenChange={setShowCreateAgenda}
        onSuccess={() => {
          setShowCreateAgenda(false);
          toast.success('Agenda created successfully');
        }}
      />

      <BoardSettingsDialog
        open={showSettingsDialog}
        onOpenChange={setShowSettingsDialog}
        currentOrganizationId={organizationId}
      />
    </div>
  );
};

const BoardMemberProfiles = ({ members, loading, onMessage, messageLoading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading board members...
      </div>
    );
  }

  if (!members || members.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">
        No board members found.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {members.map((member) => {
        const initials = member.name
          ? member.name.split(' ').slice(0, 2).map((part) => part[0]).join('').toUpperCase()
          : 'U';
        const titleLine = [member.job_title, member.company].filter(Boolean).join(' • ');
        const positionLabel = member.position ? member.position.replace(/_/g, ' ') : '';
        return (
          <div
            key={member.id}
            className="group bg-white rounded-xl border border-gray-200 p-6 shadow-sm text-center transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-lg hover:border-purple-200 hover:bg-purple-50/60"
          >
            <div className="mx-auto h-20 w-20 rounded-full bg-purple-100 flex items-center justify-center overflow-hidden">
              {member.avatar_url ? (
                <img
                  src={member.avatar_url}
                  alt={member.name || 'Board Member'}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-purple-700 font-semibold text-lg">{initials}</span>
              )}
            </div>

            <div className="mt-4">
              <h3 className="font-semibold text-gray-900">{member.name || 'Board Member'}</h3>
              {titleLine && (
                <p className="text-sm text-gray-500 mt-1">{titleLine}</p>
              )}
              <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                {member.is_executive && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                    Executive Committee
                  </span>
                )}
                {positionLabel && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 capitalize">
                    {positionLabel}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-3 text-sm text-gray-600 text-left">
              {member.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400 group-hover:text-purple-600" />
                  <a href={`mailto:${member.email}`} className="text-gray-700 group-hover:text-purple-700 hover:underline truncate">
                    {member.email}
                  </a>
                </div>
              )}
              {member.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400 group-hover:text-purple-600" />
                  <span className="text-gray-700 group-hover:text-purple-700">{member.phone}</span>
                </div>
              )}
              {member.linkedin && (
                <div className="flex items-center gap-2">
                  <Linkedin className="h-4 w-4 text-gray-400 group-hover:text-purple-600" />
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-700 group-hover:text-purple-700 hover:underline truncate"
                  >
                    {member.linkedin}
                  </a>
                </div>
              )}
            </div>

            <div className="mt-5">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => onMessage(member)}
                disabled={messageLoading?.[member.id]}
              >
                {messageLoading?.[member.id] ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <MessageSquare className="h-4 w-4 mr-2" />
                )}
                Message
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ===========================
// BOARD DASHBOARD
// ===========================
const BoardDashboard = ({ user, userRole, refreshKey = 0 }) => {
  const navigate = useNavigate();
  const isAdmin = ['owner', 'admin'].includes(userRole);
  const [meetings, setMeetings] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showAllMeetings, setShowAllMeetings] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [meetingsRes, activityRes] = await Promise.all([
        api.get('/board/meetings?upcoming_only=true'),
        api.get('/board/activity?limit=10')
      ]);
      setMeetings(meetingsRes.data.meetings || []);
      setActivity(activityRes.data.activities || []);
    } catch (err) {
      console.error('Failed to fetch board data:', err);
    } finally {
      setLoading(false);
    }
  }, [refreshKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMeetingCreated = () => {
    setShowCreateMeeting(false);
    fetchData();
    toast.success('Meeting scheduled successfully');
  };

  const getMeetingDate = (meeting) => new Date(meeting.scheduled_start || meeting.scheduled_date || meeting.start_time || 0);
  const sortedUpcomingMeetings = meetings
    .filter((meeting) => {
      const date = getMeetingDate(meeting);
      return !Number.isNaN(date.getTime()) && date >= new Date(Date.now() - 60 * 60 * 1000);
    })
    .sort((a, b) => getMeetingDate(a) - getMeetingDate(b));

  // Get next upcoming meeting by scheduled date, not creation/order returned by API
  const nextMeeting = sortedUpcomingMeetings[0] || null;

  // Other upcoming meetings (excluding next), sorted chronologically
  const upcomingMeetings = sortedUpcomingMeetings.slice(1);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="board-dashboard">
      {/* Main Content - Left 2/3 */}
      <div className="lg:col-span-2 space-y-6">
        {/* Next Meeting Card */}
        <NextMeetingCard 
          meeting={nextMeeting} 
          onViewAgenda={() => setSelectedMeeting(nextMeeting?.id)}
          onSchedule={() => setShowCreateMeeting(true)}
          isAdmin={isAdmin}
          onViewMeeting={(id) => navigate(`/governance/meetings/${id}`)}
        />

        {/* Activity Feed */}
        <ActivityFeed 
          activities={activity} 
          onRefresh={fetchData}
        />
      </div>

      {/* Sidebar - Right 1/3 */}
      <div className="space-y-4">
        {/* Upcoming Meetings */}
        <UpcomingMeetingsList 
          meetings={upcomingMeetings}
          onSelectMeeting={setSelectedMeeting}
          onSchedule={() => setShowCreateMeeting(true)}
          isAdmin={isAdmin}
          onViewMeeting={(id) => navigate(`/governance/meetings/${id}`)}
        />

        {/* Board Chat */}
        <BoardChat user={user} />
      </div>

      {/* Create Meeting Dialog */}
      <CreateMeetingDialog 
        open={showCreateMeeting}
        onOpenChange={setShowCreateMeeting}
        onSuccess={handleMeetingCreated}
      />

      {/* Meeting Detail Dialog */}
      {selectedMeeting && (
        <MeetingDetailDialog
          meetingId={selectedMeeting}
          open={!!selectedMeeting}
          onOpenChange={(open) => !open && setSelectedMeeting(null)}
          onUpdate={fetchData}
          isAdmin={isAdmin}
        />
      )}

      {/* All Meetings Dialog */}
      <AllMeetingsDialog
        open={showAllMeetings}
        onOpenChange={setShowAllMeetings}
        onSelectMeeting={(id) => { setShowAllMeetings(false); setSelectedMeeting(id); }}
        isAdmin={isAdmin}
        onSchedule={() => { setShowAllMeetings(false); setShowCreateMeeting(true); }}
      />
    </div>
  );
};

// ===========================
// NEXT MEETING CARD
// ===========================
const NextMeetingCard = ({ meeting, onViewAgenda, onSchedule, isAdmin, onViewMeeting }) => {
  if (!meeting) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
          <h2 className="text-white font-semibold text-lg">Next Meeting</h2>
        </div>
        <div className="p-8 text-center">
          <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No upcoming meetings scheduled</p>
          {isAdmin && (
            <Button onClick={onSchedule} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0">
              <Plus className="h-4 w-4 mr-2" />
              Schedule Meeting
            </Button>
          )}
        </div>
      </div>
    );
  }

  const meetingDate = new Date(meeting.scheduled_start);
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
  };

  const hasAgenda = meeting.agenda_count > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid="next-meeting-card">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
        <h2 className="text-white font-semibold text-lg">Next Meeting</h2>
      </div>

      {/* Meeting Details */}
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{meeting.title}</h3>
        <p className="text-gray-600 mb-1">{formatDate(meetingDate)} | {formatTime(meetingDate)}</p>
        <p className="text-gray-500 text-sm mb-4">
          {meeting.location || 'Location TBD'}
          {meeting.video_link && (
            <span className="ml-2">
              or <a href={meeting.video_link} className="text-purple-600 hover:underline" target="_blank" rel="noopener noreferrer">Join via Video</a>
            </span>
          )}
        </p>

        {/* Agenda Status */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-6 ${
          hasAgenda ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
        }`}>
          {hasAgenda ? (
            <>
              <Check className="h-4 w-4" />
              <span className="font-medium">Agenda Ready</span>
              <span className="text-sm opacity-75">({meeting.agenda_count} items)</span>
            </>
          ) : (
            <>
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium">Agenda Pending</span>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => onViewMeeting(meeting.id)} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0">
            <Eye className="h-4 w-4 mr-2" />
            View Meeting
          </Button>
          <Button variant="outline" onClick={onViewAgenda}>
            <ClipboardList className="h-4 w-4 mr-2" />
            View Agenda
          </Button>
          <Button variant="outline" onClick={() => downloadMeetingIcs(meeting)}>
            <Calendar className="h-4 w-4 mr-2" />
            Add to Calendar
          </Button>
          <Button variant="outline" onClick={openCalendarSubscription}>
            <ExternalLink className="h-4 w-4 mr-2" />
            Subscribe
          </Button>
        </div>
      </div>
    </div>
  );
};

// ===========================
// ACTIVITY FEED
// ===========================
const ActivityFeed = ({ activities, onRefresh }) => {
  const navigate = useNavigate();
  
  // Show actual activities only - no sample data
  const displayActivities = activities;

  const getActivityIcon = (action) => {
    switch (action) {
      case 'document_uploaded':
        return <FileText className="h-5 w-5 text-purple-600" />;
      case 'document_moved':
        return <FileText className="h-5 w-5 text-blue-500" />;
      case 'motion_proposed':
        return <Gavel className="h-5 w-5 text-orange-500" />;
      case 'minutes_finalized':
        return <ClipboardList className="h-5 w-5 text-purple-500" />;
      case 'resolution_passed':
        return <Star className="h-5 w-5 text-orange-500" />;
      case 'meeting_created':
      case 'meeting_updated':
      case 'meeting_cancelled':
        return <Calendar className="h-5 w-5 text-green-600" />;
      case 'agenda_item_added':
        return <ClipboardList className="h-5 w-5 text-blue-500" />;
      case 'comment':
        return <MessageSquare className="h-5 w-5 text-gray-400" />;
      default:
        return <Bell className="h-5 w-5 text-gray-400" />;
    }
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    return `${days}d ago`;
  };

  const formatTimestamp = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getActionVerb = (action) => {
    switch (action) {
      case 'document_uploaded': return 'uploaded';
      case 'document_moved': return 'moved';
      case 'motion_proposed': return 'proposed';
      case 'minutes_finalized': return 'finalized';
      case 'resolution_passed': return 'passed';
      case 'meeting_created': return 'scheduled';
      case 'meeting_updated': return 'updated';
      case 'meeting_cancelled': return 'cancelled';
      case 'agenda_item_added': return 'added';
      case 'comment': return 'commented on';
      default: return 'updated';
    }
  };

  const handleActivityClick = (activity) => {
    // Navigate based on entity type
    if (activity.entity_type === 'document' && activity.entity_id) {
      navigate(`/governance/documents/${activity.entity_id}`);
    } else if (activity.entity_type === 'meeting' && activity.entity_id) {
      // Could navigate to meeting details in future
      // navigate(`/governance/meetings/${activity.entity_id}`);
    }
  };

  const isClickable = (activity) => {
    return (activity.entity_type === 'document' && activity.entity_id);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200" data-testid="activity-feed">
      <div className="px-6 py-4 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">Activity Feed</h3>
      </div>
      <div className="divide-y divide-gray-100">
        {displayActivities.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No recent activity</p>
          </div>
        ) : (
          displayActivities.map((activity) => (
            <div 
              key={activity.id} 
              className={`px-6 py-4 transition-colors ${
                isClickable(activity) 
                  ? 'hover:bg-purple-50 cursor-pointer group' 
                  : 'hover:bg-gray-50'
              }`}
              onClick={() => isClickable(activity) && handleActivityClick(activity)}
              data-testid={`activity-item-${activity.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-0.5">
                  {getActivityIcon(activity.action)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900">
                    <span className="font-medium text-gray-900">{activity.user_name || 'Someone'}</span>
                    {' '}
                    <span className="text-gray-600">{getActionVerb(activity.action)}</span>
                    {' '}
                    <span className={`${isClickable(activity) ? 'text-purple-600 group-hover:text-purple-700 font-medium' : 'text-gray-900'}`}>
                      {activity.description?.replace(/^(Uploaded document: |Moved document |Added: |Cancelled: )/, '') || activity.entity_type}
                    </span>
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-gray-400">{getTimeAgo(activity.created_at)}</span>
                    <span className="text-gray-300">•</span>
                    <span className="text-xs text-gray-400">{formatTimestamp(activity.created_at)}</span>
                    {isClickable(activity) && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span className="text-xs text-purple-500 group-hover:text-purple-600">Click to view →</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {displayActivities.length > 0 && (
        <div className="px-6 py-3 border-t border-gray-100">
          <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">
            View All Activity →
          </button>
        </div>
      )}
    </div>
  );
};

// ===========================
// UPCOMING MEETINGS LIST
// ===========================
const UpcomingMeetingsList = ({ meetings, onSelectMeeting, onSchedule, isAdmin, onViewMeeting }) => {
  const [expanded, setExpanded] = useState(false);
  const COLLAPSED_COUNT = 3;
  
  const displayMeetings = expanded ? meetings : meetings.slice(0, COLLAPSED_COUNT);
  const hasMore = meetings.length > COLLAPSED_COUNT;

  const formatMeetingDate = (dateStr) => {
    const date = new Date(dateStr);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${weekday}, ${month} ${day}, ${time}`;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200" data-testid="upcoming-meetings">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 text-sm">Upcoming Meetings</h3>
        {isAdmin && (
          <Button variant="ghost" size="sm" onClick={onSchedule} className="text-purple-600 hover:text-purple-700 h-auto p-1">
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      <div className={`divide-y divide-gray-100 ${expanded ? 'max-h-64 overflow-y-auto' : ''}`}>
        {meetings.length === 0 ? (
          <div className="px-5 py-6 text-center text-gray-500 text-sm">
            No upcoming meetings
          </div>
        ) : (
          displayMeetings.map((meeting) => (
            <div
              key={meeting.id}
              className="w-full px-5 py-3 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <Calendar className="h-4 w-4 text-purple-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <button 
                    onClick={() => onViewMeeting(meeting.id)}
                    className="text-left w-full"
                  >
                    <p className="text-gray-900 font-medium text-sm truncate">{meeting.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatMeetingDate(meeting.scheduled_start)}
                    </p>
                  </button>
                </div>
                <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); onSelectMeeting(meeting.id); }}
                    className="h-7 px-2 text-xs"
                    title="Edit meeting"
                  >
                    <Edit2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {hasMore && (
        <button 
          onClick={() => setExpanded(!expanded)}
          className="w-full px-5 py-2 border-t border-gray-100 flex items-center justify-center gap-1 text-purple-600 hover:text-purple-700 hover:bg-gray-50 text-sm font-medium transition-colors"
        >
          {expanded ? (
            <>
              Show Less
              <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
            </>
          ) : (
            <>
              {meetings.length - COLLAPSED_COUNT} More
              <ChevronDown className="h-4 w-4 transition-transform" />
            </>
          )}
        </button>
      )}
    </div>
  );
};

// ===========================
// BOARD CHAT
// ===========================
const BoardChat = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [connected, setConnected] = useState(false);
  const [usingPolling, setUsingPolling] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const messagesContainerRef = React.useRef(null);
  const wsRef = React.useRef(null);
  const reconnectTimeoutRef = React.useRef(null);
  const typingTimeoutRef = React.useRef(null);
  const pollIntervalRef = React.useRef(null);
  const chatErrorCountRef = React.useRef(0);
  const fileInputRef = React.useRef(null);

  const fetchMessages = async () => {
    try {
      const response = await api.get('/board/chat');
      setMessages(response.data.messages || []);
      chatErrorCountRef.current = 0;
    } catch (err) {
      chatErrorCountRef.current += 1;
      console.log('Chat fetch error:', err);

      // Circuit breaker: avoid endless noisy CORS/network spam loops
      if (chatErrorCountRef.current >= 3 && pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setUsingPolling(false);
        setConnected(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    console.log('Starting polling fallback');
    setUsingPolling(true);
    setConnected(true); // Show as connected even when polling
    
    // Poll every 3 seconds
    if (!pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(fetchMessages, 3000);
    }
  };

  const connectWebSocket = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    if (!token) {
      startPolling();
      return;
    }

    // Build WebSocket URL from API URL
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    // Extract just the host from the URL
    const urlObj = new URL(apiUrl);
    const wsUrl = `${wsProtocol}://${urlObj.host}/api/board/chat/ws?token=${token}`;
    
    console.log('Connecting to WebSocket:', wsUrl);

    try {
      const ws = new WebSocket(wsUrl);
      
      // Set a connection timeout
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.log('WebSocket connection timeout, falling back to polling');
          ws.close();
          startPolling();
        }
      }, 5000);
      
      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket connected');
        setConnected(true);
        // Stop polling if it was running
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'message') {
          // New message received
          setMessages(prev => {
            // Check if message already exists (sent by self)
            if (prev.some(m => m.id === data.data.id)) {
              return prev;
            }
            return [...prev, data.data];
          });
        } else if (data.type === 'delete') {
          // Message deleted
          setMessages(prev => prev.filter(m => m.id !== data.data.message_id));
        } else if (data.type === 'online_users') {
          setOnlineUsers(data.users || []);
        } else if (data.type === 'typing') {
          if (data.is_typing) {
            setTypingUsers(prev => {
              if (!prev.includes(data.user_name)) {
                return [...prev, data.user_name];
              }
              return prev;
            });
            // Remove typing indicator after 3 seconds
            setTimeout(() => {
              setTypingUsers(prev => prev.filter(u => u !== data.user_name));
            }, 3000);
          } else {
            setTypingUsers(prev => prev.filter(u => u !== data.user_name));
          }
        } else if (data.type === 'system') {
          // System message (join/leave) - could display as a notification
          console.log('System:', data.text);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected', event.code, event.reason);
        setConnected(false);
        wsRef.current = null;
        
        // If closed with an error code, fall back to polling
        if (event.code >= 4000 || event.code === 1006) {
          console.log('WebSocket failed, falling back to polling');
          startPolling();
        } else {
          // Try to reconnect after 3 seconds
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 3000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        // Will trigger onclose, which will handle fallback
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to connect WebSocket:', err);
      startPolling();
    }
  };

  useEffect(() => {
    // Initial fetch of messages
    fetchMessages();
    
    // Try WebSocket first, will fall back to polling if it fails
    connectWebSocket();
    
    // Cleanup
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Scroll to bottom of messages container only (not the whole page)
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleTyping = () => {
    // Send typing indicator via WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: true }));
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing indicator after 2 seconds of no input
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: false }));
        }
      }, 2000);
    }
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || sending) return;
    
    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);
    
    // Clear typing indicator
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: false }));
    }
    
    // Optimistic update
    const tempMessage = {
      id: `temp-${Date.now()}`,
      user_name: 'You',
      user_avatar_url: user?.avatar_url || user?.profile_image_url || null,
      message: messageText,
      attachments: selectedFiles.length > 0 ? selectedFiles.map((file) => ({ filename: file.name })) : [],
      created_at: new Date().toISOString(),
      user_initials: 'ME',
      isOwn: true
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      let attachmentUrls = [];
      if (selectedFiles.length > 0) {
        setUploadingFiles(true);
        const uploads = [];
        for (const file of selectedFiles) {
          const formData = new FormData();
          formData.append('file', file);
          uploads.push(
            api.post('/upload/message-attachment', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            }).then((res) => res.data?.url).catch(() => null)
          );
        }
        const results = await Promise.all(uploads);
        attachmentUrls = results.filter(Boolean);
      }

      const response = await api.post('/board/chat', { 
        message: messageText || '(attachment)',
        attachments: attachmentUrls.length > 0 ? attachmentUrls : null
      });
      if (response.data.message) {
        // Replace temp message with real one
        setMessages(prev => prev.map(m => 
          m.id === tempMessage.id ? response.data.message : m
        ));
      }
      setSelectedFiles([]);
    } catch (err) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
      setUploadingFiles(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const renderAttachments = (attachments, isOwn) => {
    if (!attachments || attachments.length === 0) return null;
    let normalized = attachments;
    if (typeof normalized === 'string') {
      try {
        normalized = JSON.parse(normalized);
      } catch (e) {
        normalized = [normalized];
      }
    }
    if (!Array.isArray(normalized)) return null;

    return (
      <div className="mt-2 space-y-2">
        {normalized.map((attachment, index) => {
          const url = typeof attachment === 'string'
            ? attachment
            : attachment.url || attachment.file_url || attachment.proxy_url || attachment.download_url || '';
          if (!url) {
            const name = attachment.filename || attachment.file_name || attachment.name || 'attachment';
            return (
              <div key={index} className={`text-xs ${isOwn ? 'text-purple-100' : 'text-gray-600'}`}>
                {name}
              </div>
            );
          }
          const urlPath = url.split('?')[0];
          const isImage = urlPath.match(/\.(jpg|jpeg|png|gif|webp)$/i);
          const isVideo = urlPath.match(/\.(mp4|webm|ogg|mov)$/i);
          const fileName = (attachment.filename || attachment.file_name || attachment.name || urlPath.split('/').pop() || 'attachment');

          if (isImage) {
            return (
              <div key={index} className="max-w-xs">
                <img 
                  src={url} 
                  alt={fileName}
                  className="rounded-lg max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => window.open(url, '_blank')}
                />
              </div>
            );
          }
          if (isVideo) {
            return (
              <div key={index} className="max-w-xs">
                <video src={url} controls className="rounded-lg max-w-full h-auto" />
              </div>
            );
          }
          return (
            <div key={index} className={`flex items-center space-x-2 p-2 rounded border ${
              isOwn ? 'border-purple-400 bg-purple-500' : 'border-gray-200 bg-white'
            }`}>
              <Paperclip className={`w-4 h-4 ${isOwn ? 'text-purple-200' : 'text-gray-500'}`} />
              <a 
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-sm hover:underline ${isOwn ? 'text-purple-100' : 'text-blue-600'}`}
              >
                {fileName}
              </a>
            </div>
          );
        })}
      </div>
    );
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col" style={{ height: '420px' }} data-testid="board-chat">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-purple-600" />
        <h3 className="font-semibold text-gray-900 text-sm">Board Chat</h3>
        <span className={`ml-auto flex items-center gap-1 text-xs ${connected ? 'text-green-600' : 'text-yellow-600'}`}>
          <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`}></span>
          {connected ? (onlineUsers.length > 0 ? `${onlineUsers.length} online` : 'Connected') : 'Connecting...'}
        </span>
      </div>
      
      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={`flex gap-2 ${msg.isOwn ? 'flex-row-reverse' : ''}`}>
              <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium overflow-hidden ${
                msg.isOwn ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {msg.user_avatar_url ? (
                  <img
                    src={msg.user_avatar_url}
                    alt={msg.user_name || 'User avatar'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  msg.user_initials
                )}
              </div>
              <div className={`flex-1 max-w-[80%] ${msg.isOwn ? 'text-right' : ''}`}>
                <div className={`inline-block px-3 py-2 rounded-lg text-sm ${
                  msg.isOwn 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-100 text-gray-900'
                }`}>
                  {!msg.isOwn && (
                    <p className="font-medium text-xs mb-0.5 text-purple-600">{msg.user_name}</p>
                  )}
                  {msg.message && <p>{msg.message}</p>}
                  {renderAttachments(msg.attachments, msg.isOwn)}
                </div>
                <p className="text-xs text-gray-400 mt-1">{getTimeAgo(msg.created_at)}</p>
              </div>
            </div>
          ))
        )}
        {/* Typing indicator */}
        {typingUsers.length > 0 && (
          <div className="text-xs text-gray-500 italic animate-pulse">
            {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
          </div>
        )}
      </div>
      
      {/* Input */}
      <div className="p-3 border-t border-gray-100">
        {selectedFiles.length > 0 && (
          <div className="mb-3 space-y-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-2 py-1 text-xs">
                <div className="flex items-center gap-2">
                  <Paperclip className="h-3 w-3 text-gray-400" />
                  <span className="text-gray-700 truncate">{file.name}</span>
                </div>
                <button
                  className="text-gray-400 hover:text-red-500"
                  onClick={() => removeFile(index)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
          />
          <Button 
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || uploadingFiles}
            className="px-2"
            title="Attach files"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Input
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            placeholder="Type a message..."
            className="flex-1 text-sm"
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            disabled={sending}
          />
          <Button 
            size="sm" 
            onClick={handleSend}
            disabled={(!newMessage.trim() && selectedFiles.length === 0) || sending || uploadingFiles}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0 px-3"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ===========================
// CALENDAR TAB
// ===========================
const CalendarTab = ({ userRole }) => {
  const [subscriptionUrl, setSubscriptionUrl] = useState('');
  const [tokenInfo, setTokenInfo] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [urlRes, meetingsRes] = await Promise.all([
        api.get('/board/calendar/subscription-url'),
        api.get('/board/meetings')
      ]);
      setSubscriptionUrl(urlRes.data.url || '');
      setTokenInfo({
        expiresAt: urlRes.data.expires_at,
        daysUntilExpiry: urlRes.data.days_until_expiry,
        isNew: urlRes.data.is_new_token
      });
      setMeetings(meetingsRes.data.meetings || []);
    } catch (err) {
      console.error('Failed to fetch calendar data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const copySubscriptionUrl = () => {
    navigator.clipboard.writeText(subscriptionUrl);
    toast.success('Calendar URL copied to clipboard');
  };

  const regenerateToken = async () => {
    setRegenerating(true);
    try {
      const res = await api.post('/board/calendar/regenerate-token');
      setSubscriptionUrl(res.data.url);
      setTokenInfo({
        expiresAt: res.data.expires_at,
        daysUntilExpiry: res.data.days_until_expiry,
        isNew: true
      });
      toast.success('New calendar token generated. Please update your calendar subscription with the new URL.');
    } catch (err) {
      console.error('Failed to regenerate token:', err);
      toast.error('Failed to regenerate calendar token');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Calculate token status
  const isExpiringSoon = tokenInfo?.daysUntilExpiry <= 30;
  const isExpired = tokenInfo?.daysUntilExpiry <= 0;

  // Group meetings by month
  const meetingsByMonth = meetings.reduce((acc, meeting) => {
    const date = new Date(meeting.scheduled_start);
    const monthKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    if (!acc[monthKey]) acc[monthKey] = [];
    acc[monthKey].push(meeting);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="calendar-tab">
      {/* Subscribe Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-purple-100 rounded-lg">
            <Link2 className="h-6 w-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">Subscribe to Board Calendar</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add board meetings to your calendar app (Google Calendar, Outlook, Apple Calendar). 
              Your subscription is valid for 1 year.
            </p>
            
            {/* Token Status Alert */}
            {isExpired && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-700">Calendar subscription expired</p>
                  <p className="text-xs text-red-600">Please regenerate your token and update your calendar app.</p>
                </div>
              </div>
            )}
            {isExpiringSoon && !isExpired && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-700">
                    Subscription expires in {tokenInfo.daysUntilExpiry} days
                  </p>
                  <p className="text-xs text-yellow-600">Consider regenerating your token soon.</p>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-2 mb-3">
              <Input 
                value={subscriptionUrl} 
                readOnly 
                className="flex-1 bg-gray-50 text-sm"
              />
              <Button variant="outline" onClick={copySubscriptionUrl}>
                Copy URL
              </Button>
            </div>
            
            {/* Token Info & Regenerate */}
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">
                {tokenInfo && !isExpired && (
                  <>Valid until {new Date(tokenInfo.expiresAt).toLocaleDateString('en-US', { 
                    year: 'numeric', month: 'long', day: 'numeric' 
                  })}</>
                )}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={regenerateToken}
                disabled={regenerating}
                className="text-purple-600 hover:text-purple-700"
              >
                {regenerating ? (
                  <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Regenerating...</>
                ) : (
                  <><RefreshCw className="h-3 w-3 mr-1" /> Regenerate Token</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Meetings List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">All Board Meetings</h3>
        </div>
        
        {Object.keys(meetingsByMonth).length === 0 ? (
          <div className="p-12 text-center">
            <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No meetings scheduled</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {Object.entries(meetingsByMonth).map(([month, monthMeetings]) => (
              <div key={month}>
                <div className="px-6 py-3 bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-700">{month}</h4>
                </div>
                {monthMeetings.map((meeting) => {
                  const date = new Date(meeting.scheduled_start);
                  return (
                    <div 
                      key={meeting.id} 
                      className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/governance/meetings/${meeting.id}`)}
                      data-testid={`calendar-meeting-${meeting.id}`}
                    >
                      <div className="text-center min-w-[50px]">
                        <p className="text-2xl font-bold text-purple-600">{date.getDate()}</p>
                        <p className="text-xs text-gray-500 uppercase">{date.toLocaleDateString('en-US', { weekday: 'short' })}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900">{meeting.title}</p>
                        <p className="text-sm text-gray-500">
                          {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          {meeting.location && ` • ${meeting.location}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={meeting.status === 'cancelled' ? 'destructive' : 'secondary'}>
                          {meeting.status}
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/governance/meetings/${meeting.id}`);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ===========================
// ALL MEETINGS DIALOG
// ===========================
const AllMeetingsDialog = ({ open, onOpenChange, onSelectMeeting, isAdmin, onSchedule }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (open) {
      const fetchMeetings = async () => {
        setLoading(true);
        try {
          const response = await api.get('/board/meetings');
          setMeetings(response.data.meetings || []);
        } catch (err) {
          console.error('Failed to fetch meetings:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchMeetings();
    }
  }, [open]);

  const now = new Date();
  const filteredMeetings = meetings.filter(m => {
    const meetingDate = new Date(m.scheduled_start);
    if (filter === 'upcoming') return meetingDate >= now && m.status !== 'cancelled';
    if (filter === 'past') return meetingDate < now || m.status === 'completed';
    if (filter === 'cancelled') return m.status === 'cancelled';
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>All Board Meetings</DialogTitle>
        </DialogHeader>
        
        <div className="flex items-center justify-between py-2">
          <div className="flex gap-2">
            {['all', 'upcoming', 'past', 'cancelled'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  filter === f
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          {isAdmin && (
            <Button size="sm" onClick={onSchedule} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0">
              <Plus className="h-4 w-4 mr-1" />
              New Meeting
            </Button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredMeetings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No {filter} meetings found
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMeetings.map((meeting) => (
                <MeetingCard 
                  key={meeting.id} 
                  meeting={meeting} 
                  onClick={() => onSelectMeeting(meeting.id)}
                  compact
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ===========================
// MEETING CARD
// ===========================
const MeetingCard = ({ meeting, onClick, compact = false, onViewMeeting }) => {
  const meetingDate = new Date(meeting.scheduled_start);
  const endDate = meeting.scheduled_end ? new Date(meeting.scheduled_end) : null;
  const isPast = meetingDate < new Date();
  
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit'
    });
  };

  const getStatusBadge = () => {
    if (meeting.status === 'cancelled') return <Badge variant="destructive">Cancelled</Badge>;
    if (meeting.status === 'completed') return <Badge variant="secondary">Completed</Badge>;
    if (meeting.status === 'in_progress') return <Badge className="bg-green-500">In Progress</Badge>;
    if (isPast) return <Badge variant="secondary">Past</Badge>;
    return <Badge className="bg-purple-500">Scheduled</Badge>;
  };

  const handleClick = (e) => {
    if (onViewMeeting) {
      e.stopPropagation();
      onViewMeeting(meeting.id);
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <div 
      className={`bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${compact ? 'p-3' : 'p-4'}`}
      onClick={handleClick}
      data-testid={`meeting-card-${meeting.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h4 className={`font-medium text-gray-900 ${compact ? 'text-sm' : ''}`}>{meeting.title}</h4>
            {getStatusBadge()}
          </div>
          
          <div className={`flex flex-wrap gap-4 text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}>
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {formatDate(meetingDate)}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {formatTime(meetingDate)}
              {endDate && ` - ${formatTime(endDate)}`}
            </div>
            {!compact && meeting.location && (
              <div className="flex items-center gap-1">
                {meeting.location_type === 'virtual' ? (
                  <Video className="h-4 w-4" />
                ) : (
                  <MapPin className="h-4 w-4" />
                )}
                {meeting.location}
              </div>
            )}
          </div>
        </div>
        
        {onViewMeeting && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewMeeting(meeting.id);
            }}
            className="text-purple-600 hover:text-purple-700"
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </Button>
        )}
      </div>
    </div>
  );
};

// ===========================
// CREATE MEETING DIALOG
// ===========================
const CreateMeetingDialog = ({ open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meeting_type: 'regular',
    location: '',
    location_type: 'in_person',
    video_link: '',
    scheduled_start: '',
    scheduled_end: '',
    quorum_required: 0
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.scheduled_start) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      setLoading(true);
      await api.post('/board/meetings', {
        ...formData,
        scheduled_start: new Date(formData.scheduled_start).toISOString(),
        scheduled_end: formData.scheduled_end ? new Date(formData.scheduled_end).toISOString() : null
      });
      onSuccess();
      setFormData({
        title: '',
        description: '',
        meeting_type: 'regular',
        location: '',
        location_type: 'in_person',
        video_link: '',
        scheduled_start: '',
        scheduled_end: '',
        quorum_required: 0
      });
    } catch (err) {
      toast.error('Failed to create meeting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Schedule Board Meeting</DialogTitle>
          <DialogDescription>
            Create a new board meeting. You can add agenda items after creation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Meeting Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Q1 2026 Board Meeting"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Meeting objectives and notes..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="meeting_type">Meeting Type</Label>
              <Select
                value={formData.meeting_type}
                onValueChange={(value) => setFormData({ ...formData, meeting_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="special">Special</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_type">Location Type</Label>
              <Select
                value={formData.location_type}
                onValueChange={(value) => setFormData({ ...formData, location_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_person">In Person</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">
              {formData.location_type === 'virtual' ? 'Meeting Platform' : 'Location'}
            </Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder={formData.location_type === 'virtual' ? 'e.g., Zoom, Google Meet' : 'e.g., Conference Room A'}
            />
          </div>

          {(formData.location_type === 'virtual' || formData.location_type === 'hybrid') && (
            <div className="space-y-2">
              <Label htmlFor="video_link">Video Link</Label>
              <Input
                id="video_link"
                type="url"
                value={formData.video_link}
                onChange={(e) => setFormData({ ...formData, video_link: e.target.value })}
                placeholder="https://..."
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scheduled_start">Start Date & Time *</Label>
              <Input
                id="scheduled_start"
                type="datetime-local"
                value={formData.scheduled_start}
                onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scheduled_end">End Date & Time</Label>
              <Input
                id="scheduled_end"
                type="datetime-local"
                value={formData.scheduled_end}
                onChange={(e) => setFormData({ ...formData, scheduled_end: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quorum_required">Quorum Required</Label>
            <Input
              id="quorum_required"
              type="number"
              min="0"
              value={formData.quorum_required}
              onChange={(e) => setFormData({ ...formData, quorum_required: parseInt(e.target.value) || 0 })}
              placeholder="Minimum attendees required"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Schedule Meeting
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ===========================
// MEETING DETAIL DIALOG
// ===========================
const MeetingDetailDialog = ({ meetingId, open, onOpenChange, onUpdate, isAdmin }) => {
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [showAddAgenda, setShowAddAgenda] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showDocumentPicker, setShowDocumentPicker] = useState(null); // holds agenda item id
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [linkedDocuments, setLinkedDocuments] = useState({});
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentSearch, setDocumentSearch] = useState('');
  const [showReschedule, setShowReschedule] = useState(false);
  const [showMinutesPanel, setShowMinutesPanel] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    title: '',
    description: '',
    scheduled_start: '',
    scheduled_end: '',
    location: '',
    location_type: 'in_person',
    video_link: '',
    meeting_type: 'regular'
  });
  const [saving, setSaving] = useState(false);

  const fetchMeeting = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/board/meetings/${meetingId}`);
      setMeeting(response.data);
    } catch (err) {
      toast.error('Failed to load meeting details');
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  const loadMeetingDetails = useCallback(async () => {
    try {
      setLoading(true);
      setDocumentsLoading(true);
      const [meetingResponse] = await Promise.all([
        api.get(`/board/meetings/${meetingId}`),
        fetchDocuments('', { includeInventory: false, keepLoadingState: true })
      ]);
      setMeeting(meetingResponse.data);
    } catch (err) {
      toast.error('Failed to load meeting details');
    } finally {
      setDocumentsLoading(false);
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => {
    if (open && meetingId) {
      loadMeetingDetails();
    }
  }, [open, meetingId, loadMeetingDetails]);

  useEffect(() => {
    if (showDocumentPicker) {
      setDocumentSearch('');
    }
  }, [showDocumentPicker]);

  useEffect(() => {
    if (!showDocumentPicker) return;
    const term = documentSearch.trim();
    const timeout = setTimeout(() => {
      fetchDocuments(term, { includeInventory: true });
    }, 200);
    return () => clearTimeout(timeout);
  }, [documentSearch, showDocumentPicker]);

  const handleAddAgendaItem = async (itemData) => {
    try {
      await api.post(`/board/meetings/${meetingId}/agenda`, itemData);
      toast.success('Agenda item added');
      fetchMeeting();
      setShowAddAgenda(false);
    } catch (err) {
      toast.error('Failed to add agenda item');
    }
  };

  const handleDeleteAgendaItem = async (itemId) => {
    if (!window.confirm('Delete this agenda item?')) return;
    try {
      await api.delete(`/board/meetings/${meetingId}/agenda/${itemId}`);
      toast.success('Agenda item deleted');
      fetchMeeting();
    } catch (err) {
      toast.error('Failed to delete agenda item');
    }
  };

  const handleEditAgendaItem = async (itemId, updates) => {
    try {
      await api.put(`/board/agenda/${itemId}`, updates);
      toast.success('Agenda item updated');
      setEditingItem(null);
      fetchMeeting();
    } catch (err) {
      toast.error('Failed to update agenda item');
    }
  };

  async function fetchDocuments(query = '', options = {}) {
    const { includeInventory = false, keepLoadingState = false } = options;
    try {
      if (!keepLoadingState) setDocumentsLoading(true);
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      params.append('limit', '200');
      const response = await api.get(`/board/meetings/${meetingId}/documents?${params.toString()}`);
      let availableDocs = response.data.available_documents || [];

      // Only fetch the full board document inventory when the user opens/searches
      // the picker. Pulling it during initial meeting open makes the agenda render
      // with a false "No documents attached" state, then populate many seconds later.
      if (includeInventory) {
        try {
          const boardParams = new URLSearchParams();
          boardParams.append('sort_by', 'name');
          boardParams.append('sort_order', 'asc');
          boardParams.append('include_all', 'true');
          boardParams.append('limit', '200');
          boardParams.append('offset', '0');
          const boardDocsRes = await api.get(`/board/documents?${boardParams.toString()}`);
          const boardDocs = (boardDocsRes.data.documents || []).map(doc => ({
            id: doc.id,
            title: doc.name || doc.title,
            file_type: doc.mime_type || doc.file_type,
            category: doc.document_type || doc.category,
            created_at: doc.created_at
          }));
          const term = query.trim().toLowerCase();
          const merged = [...availableDocs, ...boardDocs]
            .filter(doc => !term || (doc.title || '').toLowerCase().includes(term));
          availableDocs = Array.from(new Map(merged.map(doc => [doc.id, doc])).values());
        } catch (inventoryErr) {
          console.warn('Failed to merge board document inventory:', inventoryErr);
        }
      }

      setAvailableDocuments(availableDocs);
      // Group linked documents by agenda item
      const linked = {};
      (response.data.linked_documents || []).forEach(doc => {
        const key = doc.agenda_item_id || 'general';
        if (!linked[key]) linked[key] = [];
        linked[key].push(doc);
      });
      setLinkedDocuments(linked);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      if (!keepLoadingState) setDocumentsLoading(false);
    }
  }

  const handleLinkDocument = async (documentId, agendaItemId) => {
    const selectedDoc = availableDocuments.find(doc => doc.id === documentId);
    const agendaItem = meeting?.agenda_items?.find(item => item.id === agendaItemId);
    try {
      const response = await api.post(`/board/meetings/${meetingId}/documents`, {
        document_id: documentId,
        agenda_item_id: agendaItemId
      });
      if (selectedDoc) {
        const optimisticDoc = {
          link_id: response.data?.link_id || `${agendaItemId}-${documentId}`,
          document_id: documentId,
          title: selectedDoc.title,
          file_type: selectedDoc.file_type,
          category: selectedDoc.category,
          agenda_item_id: agendaItemId,
          agenda_item_title: agendaItem?.title,
          agenda_sort_order: agendaItem?.sort_order,
          added_at: new Date().toISOString()
        };
        setLinkedDocuments(prev => ({
          ...prev,
          [agendaItemId]: [
            optimisticDoc,
            ...(prev[agendaItemId] || []).filter(doc => doc.document_id !== documentId)
          ]
        }));
        setAvailableDocuments(prev => prev.filter(doc => doc.id !== documentId));
      }
      toast.success('Document linked to agenda item');
      setShowDocumentPicker(null);
      fetchDocuments('', { includeInventory: true });
    } catch (err) {
      toast.error('Failed to link document');
    }
  };

  const handleUnlinkDocument = async (documentId) => {
    try {
      await api.delete(`/board/meetings/${meetingId}/documents/${documentId}`);
      setLinkedDocuments(prev => {
        const next = {};
        Object.entries(prev).forEach(([key, docs]) => {
          next[key] = docs.filter(doc => doc.document_id !== documentId);
        });
        return next;
      });
      toast.success('Document removed');
      fetchDocuments('', { includeInventory: true });
    } catch (err) {
      toast.error('Failed to remove document');
    }
  };

  const openLinkedDocument = (documentId) => {
    if (!documentId) return;
    onOpenChange(false);
    navigate(`/governance/documents/${documentId}`);
  };

  const allLinkedDocuments = Object.values(linkedDocuments).flat();

  const getAgendaTitleForDocument = (doc) => {
    if (doc.agenda_item_title) return `${doc.agenda_sort_order || ''}. ${doc.agenda_item_title}`.trim();
    if (doc.agenda_item_id) {
      const item = meeting?.agenda_items?.find(a => a.id === doc.agenda_item_id);
      return item ? `${item.sort_order}. ${item.title}` : 'Agenda item';
    }
    return 'General meeting document';
  };

  const handleRSVP = async (status) => {
    try {
      await api.put(`/board/meetings/${meetingId}/rsvp`, { status });
      toast.success(`RSVP updated to ${status}`);
      fetchMeeting();
    } catch (err) {
      toast.error('Failed to update RSVP');
    }
  };

  const openRescheduleDialog = () => {
    const m = meeting?.meeting;
    if (!m) return;
    
    // Format dates for datetime-local input
    const formatDateTimeLocal = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const offset = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() - offset * 60 * 1000);
      return localDate.toISOString().slice(0, 16);
    };

    setRescheduleData({
      title: m.title || '',
      description: m.description || '',
      scheduled_start: formatDateTimeLocal(m.scheduled_start),
      scheduled_end: formatDateTimeLocal(m.scheduled_end),
      location: m.location || '',
      location_type: m.location_type || 'in_person',
      video_link: m.video_link || '',
      meeting_type: m.meeting_type || 'regular'
    });
    setShowReschedule(true);
  };

  const handleSaveMeeting = async () => {
    try {
      setSaving(true);
      
      const updates = {};
      if (rescheduleData.title) updates.title = rescheduleData.title;
      if (rescheduleData.description !== undefined) updates.description = rescheduleData.description;
      if (rescheduleData.scheduled_start) updates.scheduled_start = new Date(rescheduleData.scheduled_start).toISOString();
      if (rescheduleData.scheduled_end) updates.scheduled_end = new Date(rescheduleData.scheduled_end).toISOString();
      if (rescheduleData.location !== undefined) updates.location = rescheduleData.location;
      if (rescheduleData.location_type) updates.location_type = rescheduleData.location_type;
      if (rescheduleData.video_link !== undefined) updates.video_link = rescheduleData.video_link;
      if (rescheduleData.meeting_type) updates.meeting_type = rescheduleData.meeting_type;

      await api.put(`/board/meetings/${meetingId}`, updates);
      toast.success('Meeting updated successfully');
      setShowReschedule(false);
      fetchMeeting();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error('Failed to update meeting');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="sr-only">Meeting details</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!meeting) return null;

  const meetingDate = new Date(meeting.meeting?.scheduled_start);
  const endDate = meeting.meeting?.scheduled_end ? new Date(meeting.meeting.scheduled_end) : null;
  const canEdit = meeting.meeting?.status !== 'completed' && meeting.meeting?.status !== 'cancelled';

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-xl">{meeting.meeting?.title}</DialogTitle>
              <DialogDescription>
                {meetingDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                {' • '}
                {meetingDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                {endDate && ` - ${endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
              </DialogDescription>
            </div>
            {canEdit && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={openRescheduleDialog}
                className="flex items-center gap-1"
              >
                <Edit2 className="h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Tabs */}
        <div className="border-b border-gray-200 mt-4">
          <nav className="flex gap-6">
            {[
              { key: 'details', label: 'Details' },
              { key: 'agenda', label: `Agenda (${meeting.agenda_items?.length || 0})` },
              { key: 'attendees', label: `Attendees (${meeting.attendees?.length || 0})` },
              { key: 'documents', label: `Documents (${meeting.documents?.length || 0})` },
              { key: 'minutes', label: 'Minutes' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === key
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-4">
          {activeTab === 'details' && (
            <div className="space-y-4">
              {meeting.meeting?.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
                  <p className="text-gray-600">{meeting.meeting.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Meeting Type</h4>
                  <p className="text-gray-600 capitalize">{meeting.meeting?.meeting_type}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Location Type</h4>
                  <p className="text-gray-600 capitalize">{meeting.meeting?.location_type?.replace('_', ' ')}</p>
                </div>
                {meeting.meeting?.location && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Location</h4>
                    <p className="text-gray-600">{meeting.meeting.location}</p>
                  </div>
                )}
                {meeting.meeting?.video_link && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Video Link</h4>
                    <a href={meeting.meeting.video_link} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline flex items-center gap-1">
                      Join Meeting <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Your RSVP</h4>
                <div className="flex gap-2">
                  {['accepted', 'tentative', 'declined'].map((status) => (
                    <Button
                      key={status}
                      variant={meeting.user_rsvp_status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleRSVP(status)}
                      className={meeting.user_rsvp_status === status ? 
                        status === 'accepted' ? 'bg-green-600' :
                        status === 'declined' ? 'bg-red-600' : 'bg-yellow-600'
                        : ''
                      }
                    >
                      {status === 'accepted' && <Check className="h-4 w-4 mr-1" />}
                      {status === 'declined' && <X className="h-4 w-4 mr-1" />}
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'agenda' && (
            <div className="space-y-4">
              {isAdmin && (
                <div className="flex justify-end">
                  <Button size="sm" onClick={() => setShowAddAgenda(true)} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Item
                  </Button>
                </div>
              )}

              {meeting.agenda_items?.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <ListChecks className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No agenda items yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {meeting.agenda_items?.sort((a, b) => a.sort_order - b.sort_order).map((item, index) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Agenda Item Header */}
                      <div className="flex items-start gap-3 p-3 bg-gray-50">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          {editingItem === item.id ? (
                            /* Edit Form */
                            <EditAgendaItemForm
                              item={item}
                              onSave={(updates) => handleEditAgendaItem(item.id, updates)}
                              onCancel={() => setEditingItem(null)}
                            />
                          ) : (
                            /* View Mode */
                            <>
                              <div className="flex items-center gap-2">
                                <h5 className="font-medium text-gray-900">{item.title}</h5>
                                <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded capitalize">{item.item_type}</span>
                              </div>
                              {item.description && <p className="text-sm text-gray-500 mt-1">{item.description}</p>}
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                <span className="flex items-center gap-1"><Timer className="h-3 w-3" />{item.duration_minutes} min</span>
                                {item.presenter_name && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{item.presenter_name}</span>}
                              </div>
                            </>
                          )}
                        </div>
                        {isAdmin && editingItem !== item.id && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => setEditingItem(item.id)} title="Edit">
                              <Edit2 className="h-4 w-4 text-gray-400 hover:text-purple-600" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteAgendaItem(item.id)} title="Delete">
                              <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {/* Linked Documents Section */}
                      <div className="px-3 pb-3 bg-white border-t border-gray-100">
                        <div className="flex items-center justify-between mt-2 mb-2">
                          <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            <FileText className="h-3 w-3" /> Documents
                          </span>
                          {isAdmin && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 text-xs text-purple-600 hover:text-purple-700"
                              onClick={() => setShowDocumentPicker(item.id)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Document
                            </Button>
                          )}
                        </div>
                        
                        {linkedDocuments[item.id]?.length > 0 ? (
                          <div className="space-y-1">
                            {linkedDocuments[item.id].map(doc => (
                              <div key={doc.document_id} className="flex items-center justify-between py-1.5 px-2 bg-purple-50 rounded text-sm">
                                <button
                                  type="button"
                                  onClick={() => openLinkedDocument(doc.document_id)}
                                  className="flex items-center gap-2 text-gray-700 hover:text-purple-700 min-w-0 text-left"
                                >
                                  <FileText className="h-3 w-3 text-purple-600 flex-shrink-0" />
                                  <span className="truncate">{doc.title}</span>
                                </button>
                                {isAdmin && (
                                  <button 
                                    onClick={() => handleUnlinkDocument(doc.document_id)}
                                    className="text-gray-400 hover:text-red-500"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : documentsLoading ? (
                          <p className="text-xs text-gray-400 italic">Loading documents…</p>
                        ) : (
                          <p className="text-xs text-gray-400 italic">No documents attached</p>
                        )}
                        
                        {/* Document Picker Dropdown */}
                        {showDocumentPicker === item.id && (
                          <div className="mt-2 p-2 border border-purple-200 rounded-lg bg-white shadow-lg">
                            <p className="text-xs font-medium text-gray-600 mb-2">Select a document to link:</p>
                            <div className="mb-2">
                              <Input
                                value={documentSearch}
                                onChange={(e) => setDocumentSearch(e.target.value)}
                                placeholder="Search documents..."
                                className="h-8"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                            </div>
                            <div className="max-h-40 overflow-y-auto space-y-1">
                              {availableDocuments.filter(d => !linkedDocuments[item.id]?.find(l => l.document_id === d.id)).filter(d => {
                                const term = documentSearch.trim().toLowerCase();
                                if (!term) return true;
                                return (d.title || '').toLowerCase().includes(term);
                              }).length > 0 ? (
                                availableDocuments
                                  .filter(d => !linkedDocuments[item.id]?.find(l => l.document_id === d.id))
                                  .filter(d => {
                                    const term = documentSearch.trim().toLowerCase();
                                    if (!term) return true;
                                    return (d.title || '').toLowerCase().includes(term);
                                  })
                                  .map(doc => (
                                    <button
                                      key={doc.id}
                                      onClick={() => handleLinkDocument(doc.id, item.id)}
                                      className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-purple-50 flex items-center gap-2"
                                    >
                                      <FileText className="h-3 w-3 text-gray-400" />
                                      {doc.title}
                                    </button>
                                  ))
                              ) : (
                                <p className="text-xs text-gray-400 py-2 text-center">No documents available</p>
                              )}
                            </div>
                            <div className="mt-2 pt-2 border-t flex justify-end">
                              <Button variant="ghost" size="sm" onClick={() => setShowDocumentPicker(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showAddAgenda && (
                <AddAgendaItemForm 
                  onSubmit={handleAddAgendaItem}
                  onCancel={() => setShowAddAgenda(false)}
                  nextOrder={(meeting.agenda_items?.length || 0) + 1}
                />
              )}
            </div>
          )}

          {activeTab === 'attendees' && (
            <div className="space-y-4">
              {meeting.attendees?.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No attendees have RSVP'd yet</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {meeting.attendees?.map((attendee) => (
                    <div key={attendee.user_id} className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                          {attendee.profile_image_url ? (
                            <img src={attendee.profile_image_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Users className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{attendee.name}</p>
                          <p className="text-sm text-gray-500">{attendee.email}</p>
                        </div>
                      </div>
                      <Badge variant={attendee.rsvp_status === 'accepted' ? 'default' : attendee.rsvp_status === 'declined' ? 'destructive' : 'secondary'}>
                        {attendee.rsvp_status || 'Pending'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Linked Meeting Documents</h4>
                  <p className="text-sm text-gray-500">Documents attached to the meeting or specific agenda items.</p>
                </div>
              </div>
              {allLinkedDocuments.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No documents linked yet</p>
                  <p className="text-xs text-gray-400 mt-1">Use the Agenda tab to attach documents to agenda items.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {allLinkedDocuments.map((doc) => (
                    <div key={`${doc.document_id}-${doc.agenda_item_id || 'general'}`} className="flex items-center justify-between gap-3 border rounded-lg p-3 bg-white">
                      <button
                        type="button"
                        onClick={() => openLinkedDocument(doc.document_id)}
                        className="flex items-center gap-3 min-w-0 text-left"
                      >
                        <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <FileText className="h-4 w-4 text-purple-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate hover:text-purple-700">{doc.title}</p>
                          <p className="text-xs text-gray-500 truncate">{getAgendaTitleForDocument(doc)}</p>
                        </div>
                      </button>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => handleUnlinkDocument(doc.document_id)} className="text-gray-400 hover:text-red-500">
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'minutes' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Meeting Minutes</h4>
                  <p className="text-sm text-gray-500">View and manage meeting notes, action items, and approval status</p>
                </div>
                <Button 
                  onClick={() => setShowMinutesPanel(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="open-minutes-panel-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Open Minutes Panel
                </Button>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 mb-2">Click "Open Minutes Panel" to manage meeting minutes</p>
                <p className="text-xs text-gray-400">Includes: Opening/Closing notes, Agenda item discussions, Action items, Approval workflow, and Edit history</p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Minutes Panel Modal */}
    <MinutesPanel
      meetingId={meetingId}
      agendaItems={meeting?.agenda_items || []}
      isOpen={showMinutesPanel}
      onClose={() => setShowMinutesPanel(false)}
      isLive={meeting?.meeting?.status === 'in_progress'}
      boardMembers={meeting?.attendees || []}
    />

    {/* Reschedule/Edit Meeting Dialog */}
    <Dialog open={showReschedule} onOpenChange={setShowReschedule}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Meeting</DialogTitle>
          <DialogDescription>Update meeting details, date, time, and location</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <Input
              value={rescheduleData.title}
              onChange={(e) => setRescheduleData({...rescheduleData, title: e.target.value})}
              placeholder="Meeting title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={rescheduleData.description}
              onChange={(e) => setRescheduleData({...rescheduleData, description: e.target.value})}
              placeholder="Meeting description (optional)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date & Time</label>
              <Input
                type="datetime-local"
                value={rescheduleData.scheduled_start}
                onChange={(e) => setRescheduleData({...rescheduleData, scheduled_start: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date & Time</label>
              <Input
                type="datetime-local"
                value={rescheduleData.scheduled_end}
                onChange={(e) => setRescheduleData({...rescheduleData, scheduled_end: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Type</label>
              <Select value={rescheduleData.meeting_type} onValueChange={(v) => setRescheduleData({...rescheduleData, meeting_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="special">Special</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location Type</label>
              <Select value={rescheduleData.location_type} onValueChange={(v) => setRescheduleData({...rescheduleData, location_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_person">In Person</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location / Address</label>
            <Input
              value={rescheduleData.location}
              onChange={(e) => setRescheduleData({...rescheduleData, location: e.target.value})}
              placeholder="Physical location or address"
            />
          </div>

          {(rescheduleData.location_type === 'virtual' || rescheduleData.location_type === 'hybrid') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Video Link</label>
              <Input
                value={rescheduleData.video_link}
                onChange={(e) => setRescheduleData({...rescheduleData, video_link: e.target.value})}
                placeholder="Zoom, Teams, or Google Meet link"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={() => setShowReschedule(false)}>Cancel</Button>
          <Button onClick={handleSaveMeeting} disabled={saving || !rescheduleData.title || !rescheduleData.scheduled_start}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

// ===========================
// ADD AGENDA ITEM FORM
// ===========================
const AddAgendaItemForm = ({ onSubmit, onCancel, nextOrder }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    item_type: 'discussion',
    duration_minutes: 15,
    sort_order: nextOrder
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="border border-purple-200 bg-purple-50 p-4 rounded-lg space-y-3">
      <div className="space-y-2">
        <Label htmlFor="agenda_title">Title *</Label>
        <Input id="agenda_title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Agenda item title" autoFocus />
      </div>
      <div className="space-y-2">
        <Label htmlFor="agenda_description">Description</Label>
        <Textarea id="agenda_description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description..." rows={2} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Type</Label>
          <Select value={formData.item_type} onValueChange={(value) => setFormData({ ...formData, item_type: value })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="discussion">Discussion</SelectItem>
              <SelectItem value="presentation">Presentation</SelectItem>
              <SelectItem value="vote">Vote</SelectItem>
              <SelectItem value="report">Report</SelectItem>
              <SelectItem value="break">Break</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="duration">Duration (minutes)</Label>
          <Input id="duration" type="number" min="1" value={formData.duration_minutes} onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 15 })} />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0">Add Item</Button>
      </div>
    </form>
  );
};

// ===========================
// EDIT AGENDA ITEM FORM
// ===========================
const EditAgendaItemForm = ({ item, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: item.title || '',
    description: item.description || '',
    item_type: item.item_type || 'discussion',
    duration_minutes: item.duration_minutes || 15
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="space-y-2">
        <Label htmlFor="edit_title">Title *</Label>
        <Input 
          id="edit_title" 
          value={formData.title} 
          onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
          placeholder="Agenda item title" 
          autoFocus 
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="edit_description">Description</Label>
        <Textarea 
          id="edit_description" 
          value={formData.description} 
          onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
          placeholder="Brief description..." 
          rows={2} 
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Type</Label>
          <Select value={formData.item_type} onValueChange={(value) => setFormData({ ...formData, item_type: value })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="discussion">Discussion</SelectItem>
              <SelectItem value="presentation">Presentation</SelectItem>
              <SelectItem value="vote">Vote</SelectItem>
              <SelectItem value="report">Report</SelectItem>
              <SelectItem value="break">Break</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit_duration">Duration (minutes)</Label>
          <Input 
            id="edit_duration" 
            type="number" 
            min="1" 
            value={formData.duration_minutes} 
            onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 15 })} 
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0">Save Changes</Button>
      </div>
    </form>
  );
};

// ===========================
// CREATE AGENDA DIALOG - Full Screen Builder
// ===========================
export const CreateAgendaDialog = ({ open, onOpenChange, onSuccess, preSelectedMeetingId = null }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(preSelectedMeetingId || '');
  const [mode, setMode] = useState('manual'); // 'manual', 'upload', or 'ai'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [refinementPrompt, setRefinementPrompt] = useState('');
  const [builderDocuments, setBuilderDocuments] = useState([]);
  const [builderDocumentSearch, setBuilderDocumentSearch] = useState('');
  const [attachmentPickerIndex, setAttachmentPickerIndex] = useState(null);
  const [aiPrompt, setAiPrompt] = useState({
    meeting_purpose: '',
    organization_type: 'corporate',
    duration_minutes: 60
  });
  const emptyAgendaItem = () => ({
    title: '',
    description: '',
    item_type: 'discussion',
    duration_minutes: 15,
    notes: '',
    linked_document_ids: [],
    upload_files: []
  });

  const [agendaItems, setAgendaItems] = useState([emptyAgendaItem()]);

  // Set preselected meeting when prop changes
  useEffect(() => {
    if (preSelectedMeetingId) {
      setSelectedMeeting(preSelectedMeetingId);
    }
  }, [preSelectedMeetingId, open]);

  const normalizeGeneratedItem = (item) => ({
    ...emptyAgendaItem(),
    ...item,
    notes: item.notes || '',
    linked_document_ids: item.linked_document_ids || [],
    upload_files: item.upload_files || []
  });

  const fetchBuilderDocuments = useCallback(async (query = '') => {
    if (!selectedMeeting) return;
    try {
      const params = new URLSearchParams();
      params.append('limit', '200');
      if (query) params.append('q', query);
      const response = await api.get(`/board/meetings/${selectedMeeting}/documents?${params.toString()}`);
      setBuilderDocuments(response.data.available_documents || []);
    } catch (err) {
      console.error('Failed to fetch agenda builder documents:', err);
    }
  }, [selectedMeeting]);

  useEffect(() => {
    if (open && selectedMeeting) {
      fetchBuilderDocuments(builderDocumentSearch.trim());
    }
  }, [open, selectedMeeting, builderDocumentSearch, fetchBuilderDocuments]);

  useEffect(() => {
    if (open && !preSelectedMeetingId) {
      const fetchMeetings = async () => {
        try {
          const response = await api.get('/board/meetings?upcoming_only=true');
          setMeetings(response.data.meetings || []);
        } catch (err) {
          console.error('Failed to fetch meetings:', err);
        }
      };
      fetchMeetings();
    }
  }, [open, preSelectedMeetingId]);

  const addItem = () => {
    setAgendaItems([...agendaItems, emptyAgendaItem()]);
  };

  const removeItem = (index) => {
    if (agendaItems.length > 1) {
      setAgendaItems(agendaItems.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const updated = [...agendaItems];
    updated[index][field] = value;
    setAgendaItems(updated);
  };

  const moveItem = (index, direction) => {
    if ((direction === -1 && index === 0) || (direction === 1 && index === agendaItems.length - 1)) return;
    const updated = [...agendaItems];
    const temp = updated[index];
    updated[index] = updated[index + direction];
    updated[index + direction] = temp;
    setAgendaItems(updated);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadedFile(file);
    setGenerating(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/board/agenda/parse-document-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success && response.data.agenda_items) {
        setAgendaItems(response.data.agenda_items.map(item => normalizeGeneratedItem({ ...item, notes: '' })));
        toast.success('Agenda generated from document');
      }
    } catch (err) {
      console.error('Failed to parse document:', err);
      toast.error('Failed to parse document. Please try manual entry.');
    } finally {
      setGenerating(false);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.meeting_purpose.trim()) {
      toast.error('Please describe the meeting purpose');
      return;
    }
    
    setGenerating(true);
    
    try {
      const response = await api.post('/board/agenda/generate', {
        meeting_type: 'regular',
        meeting_purpose: aiPrompt.meeting_purpose,
        organization_type: aiPrompt.organization_type,
        duration_minutes: aiPrompt.duration_minutes,
        include_standard_items: true
      });
      
      if (response.data.success && response.data.agenda_items) {
        console.log('Generated items:', response.data.agenda_items);
        setAgendaItems(response.data.agenda_items.map(item => normalizeGeneratedItem({ ...item, notes: '' })));
        toast.success('Agenda generated successfully');
      }
    } catch (err) {
      console.error('AI generation failed:', err);
      toast.error('AI generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleRefinement = async () => {
    if (!refinementPrompt.trim() || agendaItems.length === 0) return;
    
    setRefining(true);
    
    try {
      const response = await api.post('/board/agenda/refine', {
        current_agenda: agendaItems,
        refinement_request: refinementPrompt
      });
      
      if (response.data.success && response.data.agenda_items) {
        setAgendaItems(response.data.agenda_items.map(item => normalizeGeneratedItem(item)));
        setRefinementPrompt('');
        toast.success('Agenda refined');
      }
    } catch (err) {
      console.error('Refinement failed:', err);
      toast.error('Refinement failed. Please try again.');
    } finally {
      setRefining(false);
    }
  };

  const addExistingDocumentToItem = (index, documentId) => {
    if (!documentId) return;
    setAgendaItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const ids = item.linked_document_ids || [];
      return ids.includes(documentId) ? item : { ...item, linked_document_ids: [...ids, documentId] };
    }));
  };

  const removeExistingDocumentFromItem = (index, documentId) => {
    setAgendaItems(prev => prev.map((item, i) => i === index
      ? { ...item, linked_document_ids: (item.linked_document_ids || []).filter(id => id !== documentId) }
      : item
    ));
  };

  const addUploadFilesToItem = (index, files) => {
    const selectedFiles = Array.from(files || []);
    if (selectedFiles.length === 0) return;
    setAgendaItems(prev => prev.map((item, i) => i === index
      ? { ...item, upload_files: [...(item.upload_files || []), ...selectedFiles] }
      : item
    ));
  };

  const removeUploadFileFromItem = (index, fileIndex) => {
    setAgendaItems(prev => prev.map((item, i) => i === index
      ? { ...item, upload_files: (item.upload_files || []).filter((_, idx) => idx !== fileIndex) }
      : item
    ));
  };

  const getBuilderDocumentTitle = (documentId) => builderDocuments.find(doc => doc.id === documentId)?.title || 'Linked document';

  const uploadAgendaDocument = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('description', 'Uploaded from agenda builder');
    formData.append('document_type', 'agenda');
    // Do not send security fields from the agenda builder. The document upload
    // API treats non-default security values as privileged changes, and normal
    // board users should be able to upload agenda materials with server defaults.
    const response = await api.post('/board/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data?.id;
  };

  const renderAgendaAttachmentControls = (item, index) => {
    const attachedCount = (item.linked_document_ids || []).length + (item.upload_files || []).length;
    const isOpen = attachmentPickerIndex === index;
    return (
      <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 px-2 text-xs border-purple-200 text-purple-700 hover:bg-purple-50"
            onClick={() => setAttachmentPickerIndex(isOpen ? null : index)}
          >
            <Paperclip className="h-3.5 w-3.5 mr-1" />
            {attachedCount > 0 ? `${attachedCount} attached` : 'Attach'}
          </Button>
          {attachedCount > 0 && (
            <div className="flex flex-wrap gap-1.5 min-w-0">
              {(item.linked_document_ids || []).map(documentId => (
                <Badge key={documentId} variant="secondary" className="gap-1 max-w-[180px] truncate">
                  <span className="truncate">{getBuilderDocumentTitle(documentId)}</span>
                  <button type="button" onClick={() => removeExistingDocumentFromItem(index, documentId)}><X className="h-3 w-3" /></button>
                </Badge>
              ))}
              {(item.upload_files || []).map((file, fileIndex) => (
                <Badge key={`${file.name}-${fileIndex}`} className="bg-purple-100 text-purple-700 gap-1 max-w-[180px] truncate">
                  <span className="truncate">{file.name}</span>
                  <button type="button" onClick={() => removeUploadFileFromItem(index, fileIndex)}><X className="h-3 w-3" /></button>
                </Badge>
              ))}
            </div>
          )}
        </div>
        {isOpen && (
          <div className="rounded-lg border border-purple-100 bg-purple-50/60 p-3 space-y-2">
            <Input
              value={builderDocumentSearch}
              onChange={(e) => setBuilderDocumentSearch(e.target.value)}
              placeholder="Search documents..."
              className="h-8 text-xs bg-white"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Select onValueChange={(documentId) => addExistingDocumentToItem(index, documentId)}>
                <SelectTrigger className="h-8 text-xs bg-white">
                  <SelectValue placeholder="Choose existing document" />
                </SelectTrigger>
                <SelectContent>
                  {builderDocuments
                    .filter(doc => !(item.linked_document_ids || []).includes(doc.id))
                    .map(doc => (
                      <SelectItem key={doc.id} value={doc.id}>{doc.title}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <label className="h-8 inline-flex items-center justify-center rounded-md border border-purple-200 bg-white px-3 text-xs font-medium text-purple-700 hover:bg-purple-50 cursor-pointer">
                <Upload className="h-3.5 w-3.5 mr-1" />
                Upload new
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => { addUploadFilesToItem(index, e.target.files); e.target.value = ''; }}
                />
              </label>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedMeeting) {
      toast.error('Please select a meeting');
      return;
    }

    const validItems = agendaItems.filter(item => item.title.trim());
    if (validItems.length === 0) {
      toast.error('Please add at least one agenda item');
      return;
    }

    try {
      setLoading(true);
      for (let i = 0; i < validItems.length; i++) {
        const created = await api.post(`/board/meetings/${selectedMeeting}/agenda`, {
          title: validItems[i].title,
          description: validItems[i].description,
          item_type: validItems[i].item_type,
          duration_minutes: validItems[i].duration_minutes,
          notes: validItems[i].notes,
          sort_order: i + 1
        });
        const agendaItemId = created.data?.item?.id;
        if (agendaItemId) {
          for (const documentId of (validItems[i].linked_document_ids || [])) {
            await api.post(`/board/meetings/${selectedMeeting}/documents`, {
              document_id: documentId,
              agenda_item_id: agendaItemId
            });
          }
          for (const file of (validItems[i].upload_files || [])) {
            const documentId = await uploadAgendaDocument(file);
            if (documentId) {
              await api.post(`/board/meetings/${selectedMeeting}/documents`, {
                document_id: documentId,
                agenda_item_id: agendaItemId
              });
            }
          }
        }
      }
      onSuccess();
      setSelectedMeeting('');
      setAgendaItems([emptyAgendaItem()]);
      setUploadedFile(null);
      setMode('manual');
      setAiPrompt({ meeting_purpose: '', organization_type: 'corporate', duration_minutes: 60 });
    } catch (err) {
      toast.error('Failed to create agenda');
    } finally {
      setLoading(false);
    }
  };

  const formatMeetingOption = (meeting) => {
    const date = new Date(meeting.scheduled_start);
    return `${meeting.title} - ${date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`;
  };

  const totalDuration = agendaItems.reduce((sum, item) => sum + (item.duration_minutes || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
          <DialogTitle className="text-xl text-white">Create Agenda</DialogTitle>
          <DialogDescription className="text-purple-100 mt-1">
            Build a detailed agenda for your board meeting
          </DialogDescription>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Meeting Selection - Hide when preselected */}
            {!preSelectedMeetingId && (
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Select Meeting *</Label>
                <Select value={selectedMeeting} onValueChange={setSelectedMeeting}>
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Choose a meeting..." />
                  </SelectTrigger>
                  <SelectContent>
                    {meetings.length === 0 ? (
                      <div className="px-2 py-3 text-sm text-gray-500 text-center">
                        No upcoming meetings
                      </div>
                    ) : (
                      meetings.map((meeting) => (
                        <SelectItem key={meeting.id} value={meeting.id}>
                          {formatMeetingOption(meeting)}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Total Duration</Label>
                <div className="h-11 flex items-center px-4 bg-gray-50 border rounded-md">
                  <Timer className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="font-medium">{Math.floor(totalDuration / 60)}h {totalDuration % 60}m</span>
                  <span className="text-gray-400 ml-2">({agendaItems.filter(i => i.title.trim()).length} items)</span>
                </div>
              </div>
            </div>
            )}

            {/* Total Duration when preselected (show inline) */}
            {preSelectedMeetingId && (
              <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-2">
                  <Timer className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Total Duration:</span>
                  <span className="font-medium">{Math.floor(totalDuration / 60)}h {totalDuration % 60}m</span>
                  <span className="text-gray-400">({agendaItems.filter(i => i.title.trim()).length} items)</span>
                </div>
              </div>
            )}

            {/* Mode Toggle - Three Options */}
            <div className="flex items-center gap-2 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
              <button
                type="button"
                onClick={() => setMode('manual')}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all text-sm ${
                  mode === 'manual' 
                    ? 'bg-white shadow-sm text-purple-700 border border-purple-200' 
                    : 'text-gray-600 hover:bg-white/50'
                }`}
              >
                <Edit2 className="h-4 w-4 inline mr-2" />
                Build Manually
              </button>
              <button
                type="button"
                onClick={() => setMode('upload')}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all text-sm ${
                  mode === 'upload' 
                    ? 'bg-white shadow-sm text-purple-700 border border-purple-200' 
                    : 'text-gray-600 hover:bg-white/50'
                }`}
              >
                <Upload className="h-4 w-4 inline mr-2" />
                Upload & Parse
              </button>
              <button
                type="button"
                onClick={() => setMode('ai')}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all text-sm ${
                  mode === 'ai' 
                    ? 'bg-white shadow-sm text-purple-700 border border-purple-200' 
                    : 'text-gray-600 hover:bg-white/50'
                }`}
              >
                <Star className="h-4 w-4 inline mr-2" />
                AI Generate
              </button>
            </div>

            {/* Upload Section */}
            {mode === 'upload' && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-purple-200 rounded-xl p-8 text-center hover:border-purple-400 transition-colors bg-purple-50/50">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="agenda-file-upload"
                  />
                  <label htmlFor="agenda-file-upload" className="cursor-pointer">
                    {generating ? (
                      <>
                        <Loader2 className="h-12 w-12 text-purple-500 mx-auto mb-3 animate-spin" />
                        <p className="text-purple-700 font-medium">Analyzing document and generating agenda...</p>
                      </>
                    ) : uploadedFile ? (
                      <>
                        <FileText className="h-12 w-12 text-purple-500 mx-auto mb-3" />
                        <p className="text-purple-700 font-medium">{uploadedFile.name}</p>
                        <p className="text-purple-500 text-sm mt-1">Click to upload a different file</p>
                      </>
                    ) : (
                      <>
                        <Upload className="h-12 w-12 text-purple-400 mx-auto mb-3" />
                        <p className="text-purple-700 font-medium">Upload an existing agenda document</p>
                        <p className="text-purple-500 text-sm mt-1">PDF, DOC, DOCX, or TXT - AI will extract and structure the agenda</p>
                      </>
                    )}
                  </label>
                </div>
              </div>
            )}

            {/* AI Generate Section */}
            {mode === 'ai' && (
              <div className="space-y-4 p-5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg">
                    <Star className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">AI Agenda Generator</h4>
                    <p className="text-sm text-gray-500">Describe your meeting and let AI create a professional agenda</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Meeting Purpose & Topics *</Label>
                    <Textarea
                      value={aiPrompt.meeting_purpose}
                      onChange={(e) => setAiPrompt({ ...aiPrompt, meeting_purpose: e.target.value })}
                      placeholder="Describe what this meeting is about, key topics to cover, decisions to be made...&#10;&#10;Example: Quarterly board meeting to review Q4 financials, discuss strategic initiatives for next year, approve the annual budget, and vote on new committee appointments."
                      rows={4}
                      className="bg-white"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Organization Type</Label>
                      <Select
                        value={aiPrompt.organization_type}
                        onValueChange={(value) => setAiPrompt({ ...aiPrompt, organization_type: value })}
                      >
                        <SelectTrigger className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="corporate">Corporate</SelectItem>
                          <SelectItem value="nonprofit">Nonprofit</SelectItem>
                          <SelectItem value="association">Association</SelectItem>
                          <SelectItem value="cooperative">Cooperative</SelectItem>
                          <SelectItem value="government">Government</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Target Duration</Label>
                      <Select
                        value={aiPrompt.duration_minutes.toString()}
                        onValueChange={(value) => setAiPrompt({ ...aiPrompt, duration_minutes: parseInt(value) })}
                      >
                        <SelectTrigger className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">1 hour</SelectItem>
                          <SelectItem value="90">1.5 hours</SelectItem>
                          <SelectItem value="120">2 hours</SelectItem>
                          <SelectItem value="180">3 hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <Button 
                    type="button" 
                    onClick={handleAIGenerate}
                    disabled={generating || !aiPrompt.meeting_purpose.trim()}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating Agenda...
                      </>
                    ) : (
                      <>
                        <Star className="h-4 w-4 mr-2" />
                        Generate Agenda
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Generated Results Preview (for AI and Upload modes) */}
            {(mode === 'ai' || mode === 'upload') && agendaItems.length > 0 && agendaItems[0].title && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <Label className="text-sm font-medium">Generated Agenda</Label>
                    <span className="text-sm text-gray-500">({agendaItems.length} items • {Math.floor(totalDuration / 60)}h {totalDuration % 60}m)</span>
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setAgendaItems([emptyAgendaItem()]);
                      setUploadedFile(null);
                      setEditingItemIndex(null);
                    }}
                    className="text-gray-500"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear & Start Over
                  </Button>
                </div>

                {/* Editable Generated Items */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {agendaItems.map((item, index) => (
                    <div key={index} className="group">
                      {editingItemIndex === index ? (
                        // Edit Mode
                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
                          <div className="flex gap-3">
                            <div className="flex-1">
                              <Input
                                value={item.title}
                                onChange={(e) => updateItem(index, 'title', e.target.value)}
                                placeholder="Item title"
                                className="font-medium"
                                autoFocus
                              />
                            </div>
                            <Select
                              value={item.item_type}
                              onValueChange={(value) => updateItem(index, 'item_type', value)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="discussion">Discussion</SelectItem>
                                <SelectItem value="presentation">Presentation</SelectItem>
                                <SelectItem value="vote">Vote</SelectItem>
                                <SelectItem value="report">Report</SelectItem>
                                <SelectItem value="break">Break</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="flex items-center gap-1 w-24">
                              <Input
                                type="number"
                                min="1"
                                value={item.duration_minutes}
                                onChange={(e) => updateItem(index, 'duration_minutes', parseInt(e.target.value) || 15)}
                                className="text-center"
                              />
                              <span className="text-xs text-gray-500">min</span>
                            </div>
                          </div>
                          <Textarea
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            placeholder="Description..."
                            rows={2}
                            className="resize-none"
                          />
                          {renderAgendaAttachmentControls(item, index)}
                          <div className="flex justify-end gap-2">
                            <Button type="button" variant="ghost" size="sm" onClick={() => setEditingItemIndex(null)}>
                              Done
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="p-4 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-sm transition-all">
                          <div 
                            className="flex items-start gap-3 cursor-pointer"
                            onClick={() => setEditingItemIndex(index)}
                          >
                            <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="font-medium text-gray-900">{item.title}</h5>
                                <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded capitalize">{item.item_type}</span>
                                <span className="text-xs text-gray-400">{item.duration_minutes} min</span>
                                <Edit2 className="h-3 w-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                              </div>
                              {item.description && (
                                <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); removeItem(index); }}
                              className="p-1.5 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="h-4 w-4 text-gray-300 hover:text-red-500" />
                            </button>
                          </div>
                          <div className="mt-3 rounded-lg border border-purple-100 bg-purple-50/40 p-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                            {renderAgendaAttachmentControls(item, index)}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <Input
                                value={builderDocumentSearch}
                                onChange={(e) => setBuilderDocumentSearch(e.target.value)}
                                placeholder="Search existing documents..."
                                className="h-8 text-xs md:col-span-2 bg-white"
                              />
                              <Select onValueChange={(documentId) => addExistingDocumentToItem(index, documentId)}>
                                <SelectTrigger className="h-8 text-xs md:col-span-2 bg-white">
                                  <SelectValue placeholder="Link existing document..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {builderDocuments
                                    .filter(doc => !(item.linked_document_ids || []).includes(doc.id))
                                    .map(doc => (
                                      <SelectItem key={doc.id} value={doc.id}>{doc.title}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {((item.linked_document_ids || []).length > 0 || (item.upload_files || []).length > 0) && (
                              <div className="flex flex-wrap gap-2">
                                {(item.linked_document_ids || []).map(documentId => (
                                  <Badge key={documentId} variant="secondary" className="gap-1">
                                    {getBuilderDocumentTitle(documentId)}
                                    <button type="button" onClick={() => removeExistingDocumentFromItem(index, documentId)}><X className="h-3 w-3" /></button>
                                  </Badge>
                                ))}
                                {(item.upload_files || []).map((file, fileIndex) => (
                                  <Badge key={`${file.name}-${fileIndex}`} className="bg-purple-100 text-purple-700 gap-1">
                                    Upload: {file.name}
                                    <button type="button" onClick={() => removeUploadFileFromItem(index, fileIndex)}><X className="h-3 w-3" /></button>
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Add Item Button */}
                  <button
                    type="button"
                    onClick={() => {
                      setAgendaItems([...agendaItems, { ...emptyAgendaItem(), title: 'New Item' }]);
                      setEditingItemIndex(agendaItems.length);
                    }}
                    className="w-full py-3 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 hover:border-purple-300 hover:text-purple-600 transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    Add Item
                  </button>
                </div>

                {/* AI Refinement Chat */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100 p-4">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <div className="p-1.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded">
                      <MessageSquare className="h-4 w-4 text-white" />
                    </div>
                    <div className="text-center">
                      <h5 className="font-medium text-gray-900 text-sm">Refine with AI</h5>
                      <p className="text-xs text-gray-500">Tell me how to improve the agenda</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <Textarea
                      value={refinementPrompt}
                      onChange={(e) => setRefinementPrompt(e.target.value)}
                      placeholder='Describe changes you want...&#10;&#10;Examples:&#10;• "Add a 10-minute break after item 3"&#10;• "Make the budget discussion longer"&#10;• "Combine items 2 and 3 into one"&#10;• "Add time for Q&A after each presentation"'
                      rows={4}
                      className="bg-white text-sm resize-none"
                    />
                    {/* Quick suggestions - centered */}
                    <div className="flex flex-wrap justify-center gap-2">
                      {['Add a break', 'Make it shorter', 'Add opening remarks', 'More discussion time'].map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => setRefinementPrompt(suggestion)}
                          className="px-3 py-1 text-xs bg-white border border-purple-200 rounded-full text-purple-700 hover:bg-purple-100 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                    {/* Refine button - centered */}
                    <div className="flex justify-center">
                      <Button 
                        type="button"
                        onClick={handleRefinement}
                        disabled={refining || !refinementPrompt.trim()}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0 px-8"
                      >
                        {refining ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Star className="h-4 w-4 mr-2" />
                            Refine
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Manual Build Mode - Full Editor */}
            {mode === 'manual' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Agenda Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} className="text-purple-600 border-purple-200 hover:bg-purple-50">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Item
                  </Button>
                </div>

                <div className="space-y-4">
                  {agendaItems.map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-4">
                        {/* Item Number & Controls */}
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full flex items-center justify-center text-lg font-semibold">
                            {index + 1}
                          </div>
                          <div className="flex flex-col gap-0.5 mt-2">
                            <button
                              type="button"
                              onClick={() => moveItem(index, -1)}
                              disabled={index === 0}
                              className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                            >
                              <ChevronDown className="h-4 w-4 rotate-180 text-gray-400" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveItem(index, 1)}
                              disabled={index === agendaItems.length - 1}
                              className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                            >
                              <ChevronDown className="h-4 w-4 text-gray-400" />
                            </button>
                          </div>
                        </div>

                        {/* Item Content */}
                        <div className="flex-1 space-y-4">
                          {/* Title Row */}
                          <div className="flex gap-4">
                            <div className="flex-1">
                              <Label className="text-xs text-gray-500 mb-1 block">Title *</Label>
                              <Input
                                value={item.title}
                                onChange={(e) => updateItem(index, 'title', e.target.value)}
                                placeholder="Agenda item title"
                                className="font-medium"
                              />
                            </div>
                            <div className="w-40">
                              <Label className="text-xs text-gray-500 mb-1 block">Type</Label>
                              <Select
                                value={item.item_type}
                                onValueChange={(value) => updateItem(index, 'item_type', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="discussion">Discussion</SelectItem>
                                  <SelectItem value="presentation">Presentation</SelectItem>
                                  <SelectItem value="vote">Vote</SelectItem>
                                  <SelectItem value="report">Report</SelectItem>
                                  <SelectItem value="break">Break</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="w-28">
                              <Label className="text-xs text-gray-500 mb-1 block">Duration</Label>
                              <div className="flex items-center">
                                <Input
                                  type="number"
                                  min="1"
                                  value={item.duration_minutes}
                                  onChange={(e) => updateItem(index, 'duration_minutes', parseInt(e.target.value) || 15)}
                                  className="text-center"
                                />
                                <span className="text-sm text-gray-500 ml-2">min</span>
                              </div>
                            </div>
                          </div>

                          {/* Description */}
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block">Description</Label>
                            <Textarea
                              value={item.description}
                              onChange={(e) => updateItem(index, 'description', e.target.value)}
                              placeholder="Detailed description of this agenda item, objectives, and any background information..."
                              rows={3}
                              className="resize-none"
                            />
                          </div>

                          {/* Notes */}
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block">Preparation Notes</Label>
                            <Textarea
                              value={item.notes}
                              onChange={(e) => updateItem(index, 'notes', e.target.value)}
                              placeholder="Notes for preparation, documents to review, questions to address..."
                              rows={2}
                              className="resize-none bg-yellow-50 border-yellow-200 focus:border-yellow-400"
                            />
                          </div>

                          {/* Documents */}
                          {renderAgendaAttachmentControls(item, index)}
                        </div>

                        {/* Delete Button */}
                        {agendaItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                          >
                            <Trash2 className="h-5 w-5 text-gray-300 group-hover:text-red-500" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add More Button */}
                <button
                  type="button"
                  onClick={addItem}
                  className="w-full py-4 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 hover:border-purple-300 hover:text-purple-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="h-5 w-5" />
                  Add Another Agenda Item
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {agendaItems.filter(i => i.title.trim()).length} of {agendaItems.length} items ready
            {!preSelectedMeetingId && !selectedMeeting && agendaItems.filter(i => i.title.trim()).length > 0 && (
              <span className="text-amber-600 ml-2">• Select a meeting above to save</span>
            )}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={loading || !selectedMeeting || agendaItems.filter(i => i.title.trim()).length === 0} 
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0 px-8 disabled:opacity-50"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Agenda
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ===========================
// COMMITTEES DASHBOARD
// ===========================
const CommitteesDashboard = ({ user, committees, userRole }) => {
  const navigate = useNavigate();
  const isAdmin = ['owner', 'admin'].includes(userRole);
  
  if (committees.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
        <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-lg font-medium text-gray-900 mb-2">No Committees</h2>
        <p className="text-gray-500 mb-4">You haven't been added to any committees yet.</p>
        {isAdmin && (
          <a href="/governance/settings?tab=committees" className="text-purple-600 hover:text-purple-700 text-sm">
            Create committees in Governance Settings →
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {committees.map(committee => {
          const color = getCommitteeColor(committee.name, committee.color_hex);
          return (
          <div
            key={committee.id}
            className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate(`/governance/committees/${committee.id}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                navigate(`/governance/committees/${committee.id}`);
              }
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-2 rounded-lg" style={{ background: color.soft }}>
                <Building2 className="h-6 w-6" style={{ color: color.accent }} />
              </div>
              {committee.role && (
                <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full capitalize">{committee.role}</span>
              )}
            </div>
            <h3 className="font-medium text-gray-900 mb-1">{committee.name}</h3>
            {committee.description && <p className="text-sm text-gray-500 line-clamp-2">{committee.description}</p>}
            <div className="mt-4 flex items-center text-sm text-gray-400">
              <ChevronRight className="h-4 w-4 ml-auto" />
            </div>
          </div>
        );
        })}
      </div>
    </div>
  );
};

export default Governance;
